import axios from 'axios';

/**
 * Genera un documento draft
 * @param {object} params
 * @param {string} params.tipo - Tipo de documento
 * @param {object} params.data - Datos para rellenar el documento
 * @param {string} params.pedido_uuid - UUID del pedido (opcional)
 * @param {string} params.encargo_uuid - UUID del encargo (opcional)
 * @returns {Promise<object>} - Documento generado
 */
export const generarDocumento = async ({
	tipo,
	data,
	pedido_uuid,
	encargo_uuid,
}) => {
	try {
		const response = await axios.post(
			'/documentos/generar',
			{
				tipo,
				data,
				pedido_uuid,
				encargo_uuid,
			},
			{
				retry: 3,
				retryDelay: 1000,
			},
		);
		return response.data.data;
	} catch (error) {
		console.error('Error generando documento:', error);
		throw error.response;
	}
};

/**
 * Firma un documento
 * @param {string} documento_uuid - UUID del documento
 * @param {object} signatures - Objeto con las firmas { signatureField: base64PngImage }
 * @returns {Promise<object>} - Documento firmado
 */
export const firmarDocumento = async (documento_uuid, signatures) => {
	try {
		const response = await axios.post(
			`/documentos/${documento_uuid}/firmar`,
			{ signatures },
			{
				retry: 3,
				retryDelay: 1000,
			},
		);
		return response.data.data;
	} catch (error) {
		console.error('Error firmando documento:', error);
		throw error.response;
	}
};

/**
 * Obtiene un documento por UUID
 * @param {string} uuid - UUID del documento
 * @returns {Promise<object>} - Documento
 */
export const getDocumento = async uuid => {
	try {
		const response = await axios.get(`/documentos/${uuid}`, {
			retry: 3,
			retryDelay: 1000,
		});
		return response.data.data;
	} catch (error) {
		console.error('Error obteniendo documento:', error);
		throw error.response;
	}
};

/**
 * Obtiene documentos de un pedido
 * @param {string} pedido_uuid - UUID del pedido
 * @returns {Promise<Array>} - Lista de documentos
 */
export const getDocumentosByPedido = async pedido_uuid => {
	try {
		const response = await axios.get(`/documentos/pedido/${pedido_uuid}`, {
			retry: 3,
			retryDelay: 1000,
		});
		return response.data.data;
	} catch (error) {
		console.error('Error obteniendo documentos del pedido:', error);
		throw error.response;
	}
};

/**
 * Obtiene documentos de un encargo
 * @param {string} encargo_uuid - UUID del encargo
 * @returns {Promise<Array>} - Lista de documentos
 */
export const getDocumentosByEncargo = async encargo_uuid => {
	try {
		const response = await axios.get(
			`/documentos/encargo/${encargo_uuid}`,
			{
				retry: 3,
				retryDelay: 1000,
			},
		);
		return response.data.data;
	} catch (error) {
		console.error('Error obteniendo documentos del encargo:', error);
		throw error.response;
	}
};

/**
 * Cancela un documento
 * @param {string} uuid - UUID del documento
 * @returns {Promise<object>} - Documento cancelado
 */
export const cancelarDocumento = async uuid => {
	try {
		const response = await axios.patch(`/documentos/${uuid}/cancelar`, {
			retry: 3,
			retryDelay: 1000,
		});
		return response.data.data;
	} catch (error) {
		console.error('Error cancelando documento:', error);
		throw error.response;
	}
};

/**
 * Elimina un documento borrador
 * @param {string} uuid - UUID del documento
 * @returns {Promise<void>}
 */
export const eliminarDocumento = async uuid => {
	try {
		const response = await axios.delete(`/documentos/${uuid}`, {
			retry: 3,
			retryDelay: 1000,
		});
		return response.data;
	} catch (error) {
		console.error('Error eliminando documento:', error);
		throw error.response;
	}
};

/**
 * Actualiza un documento borrador
 * @param {string} uuid - UUID del documento
 * @param {object} data - Nuevos datos del documento
 * @returns {Promise<object>} - Documento actualizado
 */
export const actualizarDocumento = async (uuid, data) => {
	try {
		const response = await axios.patch(
			`/documentos/${uuid}`,
			{ data },
			{
				retry: 3,
				retryDelay: 1000,
			},
		);
		return response.data.data;
	} catch (error) {
		console.error('Error actualizando documento:', error);
		throw error.response;
	}
};
